<script setup lang="ts">
import { computed, toRef } from "vue";
import { useI18n } from "vue-i18n";

import { cardActions } from "@/action/cardActions";
import ActionMenuDropdown from "@/components/ActionMenu/ActionMenuDropdown.vue";
import { boardKey } from "@/components/board/injectKeys";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import type { BoardCard } from "@/model/card";
import { injectStrict } from "@/utils/vue";

import ActionConfirmModal from "../ActionConfirmModal.vue";
import BulkDeleteStickies from "../BulkDeleteStickies.vue";
import DeleteAlmStickies from "../DeleteAlmStickies.vue";
import { useBulkActionTracking } from "../useBulkActionTracking";
import { useDeleteEligibility } from "../useEligibility";

const props = defineProps<{
  cards: BoardCard[];
  /** When true, single non-ALM delete skips the confirmation modal (server setting) */
  skipConfirm: boolean;
}>();
const emit = defineEmits<{ delete: [cards: BoardCard[]] }>();

const board = injectStrict(boardKey);
const { t } = useI18n();
const cards = toRef(props, "cards");
const {
  shouldRender,
  shouldRenderDisabled,
  deletable,
  blockedAlm,
  blockedAlmReason,
  blockedMirror,
  almName,
  isStickyDeletableOnBoard,
} = useDeleteEligibility(cards, board);

const single = computed(() => cards.value.length === 1);
const singleCard = computed(() => cards.value[0]);
const singleIsAlm = computed(() => single.value && !!singleCard.value?.data.almId);

const disabledTooltip = computed(() => {
  const key =
    blockedAlmReason.value === "multi"
      ? /*$t*/ "action.deleteAlmSticky.deletionBlocked.multi"
      : /*$t*/ "action.deleteAlmSticky.deletionBlocked.config";
  return t(key, { almName: almName.value });
});

const { trackOpened, trackConfirmed } = useBulkActionTracking("delete", cards, [
  ["blocked-alm", blockedAlm],
  ["blocked-mirror", blockedMirror],
]);

function onDelete() {
  trackConfirmed();
  emit("delete", deletable.value);
}
</script>

<template>
  <!-- Single non-ALM: confirm-free fast-path (server setting) -->
  <IconButton
    v-if="shouldRender && single && !singleIsAlm && skipConfirm"
    :icon="cardActions.delete.data.icon!"
    :text="$t(cardActions.delete.data.name)"
    :aria-label="$t(cardActions.delete.data.label)"
    @click="onDelete"
  />

  <!-- Dropdown with confirmation: single non-ALM / single ALM / bulk -->
  <!-- Enable teleport for bulk action modals - modal is very tall, auto-flipping doesn't work without teleporting -->
  <ActionMenuDropdown
    v-else-if="shouldRender"
    :action="cardActions.delete"
    width="auto"
    max-height="auto"
    :disable-teleport="single"
    @open="trackOpened"
  >
    <ActionConfirmModal
      v-if="single && !singleIsAlm"
      :title="$t('action.deleteSticky', 1)"
      :description="$t('action.deleteSticky.description', 1)"
      :primary-label="$t('general.delete')"
      primary-icon="general/delete"
      primary-test-id="delete-button"
      destructive
      @primary="onDelete"
    />
    <DeleteAlmStickies
      v-else-if="single"
      :is-deletable="isStickyDeletableOnBoard"
      :alm-id="singleCard.data.almId!"
      @delete="onDelete"
    />
    <BulkDeleteStickies
      v-else
      :deletable="deletable"
      :blocked-alm="blockedAlm"
      :blocked-alm-reason="blockedAlmReason"
      :blocked-mirror="blockedMirror"
      @delete="onDelete"
    />
  </ActionMenuDropdown>

  <!-- Disabled (multi-ALM bulk or board config blocks ALM deletion) -->
  <IconButton
    v-else-if="shouldRenderDisabled"
    disabled
    :icon="cardActions.delete.data.icon!"
    :text="disabledTooltip"
    :aria-label="disabledTooltip"
  />
</template>
